'use client'

import React from 'react'
import type { UseFormRegister, FieldErrors } from 'react-hook-form'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'

interface GuestForm {
  fullName: string
  gender: string
  phone: string
  email: string
}

interface GuestInfoProps {
  register: UseFormRegister<GuestForm>
  errors: FieldErrors<GuestForm>
  disablePhone?: boolean
}

export default function GuestInfo({ register, errors, disablePhone }: GuestInfoProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.value = event.target.value.trim()
  }

  return (
    <div className="w-full">
      {/* Giới tính */}
      <div className="flex justify-between sm:grid sm:gap-2">
        <label className="font-lora text-sm font-medium text-[#2D2E32] sm:text-sm">
          {t('guestInfo.gender')} <span className="text-[#E02424]">*</span>
        </label>
        <div className="flex items-center gap-4">
          <label className="flex cursor-pointer items-center">
            <input
              type="radio"
              value="Mr"
              {...register('gender')}
              defaultChecked
              className="peer hidden"
            />
            <div className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-gray-400 peer-checked:border-primary-500 peer-checked:bg-primary-500">
              <div className="h-3 w-3 rounded-full border-2 border-white"></div>
            </div>
            <span className="ml-2 text-xs sm:text-sm">{t('guestInfo.mr')}</span>
          </label>

          <label className="flex cursor-pointer items-center">
            <input type="radio" value="Ms" {...register('gender')} className="peer hidden" />
            <div className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-gray-400 peer-checked:border-primary-500 peer-checked:bg-primary-500">
              <div className="h-3 w-3 rounded-full border-2 border-white"></div>
            </div>
            <span className="ml-2 text-xs sm:text-sm">{t('guestInfo.ms')}</span>
          </label>
        </div>
      </div>
      {errors.gender && <p className="mt-1 text-xs text-red-500">{errors.gender.message}</p>}

      {/* Họ và tên */}
      <div className="pt-3">
        <label className="font-lora text-sm font-medium text-[#2D2E32] sm:text-sm">
          {t('guestInfo.fullName')} <span className="text-[#E02424]">*</span>
        </label>
        <input
          type="text"
          {...register('fullName')}
          placeholder={t('guestInfo.fullNamePlaceholder')}
          className={`h-10 w-full rounded-[4px] border focus:border-primary-500 focus:ring-primary-500 sm:mt-2 ${errors.fullName ? 'border-red-500' : 'border-[#CED0D3]'} placeholder:text-[#ABAFB5]`}
          maxLength={250}
          onBlur={handleBlur}
        />
        {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName.message}</p>}
      </div>

      {/* Số điện thoại */}
      <div className="gap-4 sm:flex">
        <div className="w-full pt-3 sm:w-[50%]">
          <label className="mt-2 font-lora text-sm font-medium text-[#2D2E32] sm:text-sm">
            {t('guestInfo.phone')} <span className="text-[#E02424]">*</span>
          </label>
          <input
            type="text"
            {...register('phone')}
            placeholder={t('guestInfo.phonePlaceholder')}
            readOnly={disablePhone}
            // readOnly alone still lets the field be clicked/tabbed into
            // (blinking caret, focus ring) even though typing does nothing —
            // block that entirely: no keyboard focus, no mousedown focus,
            // and no focus ring classes when the field is disabled.
            tabIndex={disablePhone ? -1 : undefined}
            onMouseDown={disablePhone ? (e) => e.preventDefault() : undefined}
            onFocus={disablePhone ? (e) => e.target.blur() : undefined}
            className={`h-10 w-full rounded-[4px] border sm:mt-2 ${errors.phone ? 'border-red-500' : 'border-[#CED0D3]'} placeholder:text-[#ABAFB5] ${disablePhone ? 'cursor-not-allowed bg-[#F3F3F3] text-[#666B74]' : 'focus:border-primary-500 focus:ring-primary-500'}`}
            onBlur={handleBlur}
            maxLength={15}
            minLength={8}
          />
          {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
        </div>

        {/* Email */}
        <div className="w-full pt-3 sm:w-[50%]">
          <label className="font-lora text-sm font-medium text-[#2D2E32] sm:text-sm">
            Email <span className="text-[#E02424]">*</span>
          </label>
          <input
            type="text"
            {...register('email')}
            placeholder={t('guestInfo.emailPlaceholder')}
            className={`h-10 w-full rounded-[4px] border ${errors.email ? 'border-red-500' : 'border-[#CED0D3]'} placeholder:text-[#ABAFB5] focus:border-primary-500 focus:ring-primary-500 sm:mt-2`}
            onBlur={handleBlur}
            maxLength={255}
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>
      </div>
    </div>
  )
}
